import {useContext, useEffect, useRef, useState} from 'react';
import AppContext from "../../../context";
import {useSearchParams} from 'react-router-dom';
import { trackMetaEvent, retrievePendingPurchase } from '../../../utils/metaPixel';

import Meta from "../../components/Meta";
import Header from "../../components/Header";
import Data from "../../../repository";
import Loading from "../../components/Loading";
import ChoosePlan from "./ChoosePlan";
import CurrentMembership from "./CurrentMembership";
import WorldpayCurrentMembership from "./WorldpayCurrentMembership";
import SumupCurrentMembership from "./SumupCurrentMembership";

function Membership() {
    const context = useContext(AppContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [memberships, setMemberships] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const purchaseTrackedRef = useRef(false);

    const isWorldpay = context.domain?.paymentProcessor === 'Worldpay';
    const isSumup = context.domain?.paymentProcessor === 'SumUp';
    const isPaidExtrasContext = context.domain?.businessType === 'sessions';

    useEffect(async () => {
        await handlePaymentReturn();
        setMemberships(await fetchMemberships());
        setLoading(false);
    }, [])

    useEffect(() => {
        if (paymentStatus !== 'success' || purchaseTrackedRef.current) return;
        const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
        if (!pixelId) return;
        purchaseTrackedRef.current = true;
        const pending = retrievePendingPurchase('membership');
        trackMetaEvent({
            pixelId,
            eventName: 'Purchase',
            params: {
                content_type: 'membership',
                content_ids: pending?.planId ? [pending.planId] : undefined,
                content_name: pending?.name,
                value: pending?.value || 0,
                currency: pending?.currency || context.domain?.metaCurrency || 'GBP'
            }
        });
    }, [paymentStatus]);

    const handlePaymentReturn = async () => {
        const paymentStatusParam = searchParams.get('paymentStatus');
        const setupId = searchParams.get('setupId');
        const transactionId = searchParams.get('transactionId');

        // Handle Worldpay or SumUp return
        if ((isWorldpay || isSumup) && paymentStatusParam) {
            if (paymentStatusParam === 'success') {
                if (setupId || transactionId) {
                    setVerifying(true);
                    try {
                        // Verify the payment/subscription with backend
                        if (setupId) {
                            if (setupId.startsWith('upd_')) {
                                await Data.verifyWorldpayPaymentUpdate(setupId);
                                setPaymentStatus('update_success');
                            } else if (isSumup) {
                                await Data.verifySumupSubscription(setupId);
                            } else {
                                await Data.verifyWorldpaySubscription(setupId);
                            }
                        } else if (transactionId) {
                            if (isSumup) {
                                await Data.verifySumupPayment(transactionId);
                            } else {
                                await Data.verifyWorldpayPayment(transactionId);
                            }
                        }
                        setPaymentStatus('success');
                    } catch (error) {
                        console.error('Payment verification failed:', error);
                        setPaymentStatus('error');
                    } finally {
                        setVerifying(false);
                        // Clear URL parameters
                        searchParams.delete('paymentStatus');
                        searchParams.delete('setupId');
                        searchParams.delete('transactionId');
                        setSearchParams(searchParams);
                    }
                } else {
                    // Already verified (e.g. SumUp widget flow) — just show success
                    setPaymentStatus('success');
                    searchParams.delete('paymentStatus');
                    setSearchParams(searchParams);
                }
            } else if (paymentStatusParam === 'failed' || paymentStatusParam === 'pending') {
                setPaymentStatus('failed');
                // Clear URL parameters
                searchParams.delete('paymentStatus');
                searchParams.delete('setupId');
                searchParams.delete('transactionId');
                setSearchParams(searchParams);
            }
        }

        // Handle Stripe return
        if (!isWorldpay && !isSumup && searchParams.get('payment') === 'success') {
            setPaymentStatus('success');
            // Clear URL parameters
            searchParams.delete('payment');
            setSearchParams(searchParams);
        }
    }

    const fetchMemberships = async () => {
        if (isWorldpay) {
            const result = await Data.fetchWorldpaySubscriptions();
            return result.data.filter(sub => sub.type === 'membership');
        }
        if (isSumup) {
            const result = await Data.fetchSumupSubscriptions();
            return result.data.filter(sub => sub.type === 'membership');
        }
        const result = await Data.fetchMemberships();
        return result.data;
    }

    const hasActiveMembership = () => {
        if (memberships.length === 0) return false;
        const current = memberships[memberships.length - 1];

        if (isWorldpay || isSumup) {
            return ['active', 'paused', 'past_due', 'pending_cancellation'].includes(current.status);
        }

        return current.status === 'active' || current.status === 'past_due';
    }

    if(loading || verifying) return <Loading button={false} />

    const renderPaymentStatusMessage = () => {
        if (!paymentStatus) return null;

        if (paymentStatus === 'update_success') {
            return (
                <div style={{
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    color: '#155724',
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    textAlign: 'center'
                }}>
                    <strong>Payment Method Updated!</strong>
                    <p>Your new payment details have been saved. Your membership will renew automatically.</p>
                </div>
            );
        }

        if (paymentStatus === 'success') {
            return (
                <div style={{
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    color: '#155724',
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    textAlign: 'center'
                }}>
                    <strong>Payment Successful!</strong>
                    <p>{isPaidExtrasContext ? 'Paid Extras has been activated on your account.' : 'Your membership has been activated. You now have access to the facilities.'}</p>
                </div>
            );
        }

        if (paymentStatus === 'failed') {
            return (
                <div style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    color: '#721c24',
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    textAlign: 'center'
                }}>
                    <strong>Payment Failed</strong>
                    <p>Unfortunately, your payment could not be processed. Please try again or contact support if the problem persists.</p>
                </div>
            );
        }

        if (paymentStatus === 'error') {
            return (
                <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    color: '#856404',
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    textAlign: 'center'
                }}>
                    <strong>Verification Error</strong>
                    <p>There was an issue verifying your payment. Please refresh the page or contact support if your payment was successful but not showing.</p>
                </div>
            );
        }

        return null;
    }

    return (
        <>
            <Meta title={isPaidExtrasContext ? "Paid Extras" : "Membership"} />

            <Header heading={isPaidExtrasContext ? "Paid Extras" : "Membership"} />
            <main>
                {renderPaymentStatusMessage()}
                {!hasActiveMembership() ? (
                    <ChoosePlan />
                ) : isWorldpay ? (
                    <WorldpayCurrentMembership />
                ) : isSumup ? (
                    <SumupCurrentMembership />
                ) : (
                    <CurrentMembership />
                )}
            </main>
        </>
    );
}

export default Membership;
